// The master Catalog tab of the Master Datasheet Composer: the catalog's Index Sheets and
// manufacturers, Compare/Review against the project databases, and Update Template DB.
// Shown only to holders of utilities.ds.master.catalog; every endpoint it calls is gated on it.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Trash2 } from 'lucide-react';
import { dsMasterCatalogApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import SearchClearIcon from '../../components/common/SearchClearIcon';
import { applySearch, SEARCH_HINT } from '../../utils/listSearch';
import { dsErrMsg } from './dsBoxes';
import { statusBadgeClass } from './masterCatalog';
import { ConfirmModal } from './CatalogModals';
import CatalogCompareSection from './CatalogCompareSection';
import TemplateDbSection from './TemplateDbSection';
import StuiSelect from '../../components/common/StuiSelect';

const SECTIONS = [
  ['sheets', 'Index Sheets'],
  ['manufacturers', 'Manufacturers'],
  ['compare', 'Compare / Review'],
  ['templatedb', 'Update Template DB'],
];

const SHEET_SEARCH = ['indexSheet', 'description', 'manufacturer', 'manufacturerCode', 'type', 'status', 'dsIndex'];
const MFR_SEARCH = ['indexCode', 'manufacturer', 'supplierName', 'city', 'state'];

const revOf = key => (key ? key.slice(key.lastIndexOf('_') + 1) : '');

export default function MasterCatalogTab({ allDsIndex = [] }) {
  const { notify } = useNotifications();
  const { currentUser } = useAuth();
  const auditCfg = { headers: { 'X-Changed-By': currentUser?.email || '' } };

  const [section, setSection] = useState('sheets');
  const [sheets, setSheets] = useState([]);
  const [mfrs, setMfrs] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(null);     // id of the sheet being written
  const [deleting, setDeleting] = useState(null); // the sheet awaiting delete confirmation
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, m, st] = await Promise.all([
        dsMasterCatalogApi.getSheets(), dsMasterCatalogApi.getManufacturers(), dsMasterCatalogApi.getStatuses(),
      ]);
      setSheets(s.data ?? []);
      setMfrs(m.data ?? []);
      setStatuses(st.data ?? []);
    } catch (e) { notify.error(dsErrMsg(e, 'Failed to load the catalog.')); }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const shownSheets = useMemo(() => applySearch(sheets, search, SHEET_SEARCH), [sheets, search]);
  const shownMfrs = useMemo(() => applySearch(mfrs, search, MFR_SEARCH), [mfrs, search]);

  const updateSheet = async (sheet, body, what) => {
    setSaving(sheet.id);
    try {
      await dsMasterCatalogApi.updateSheet(sheet.id, body, auditCfg);
      notify.success(`${sheet.indexSheet}: ${what}.`);
      await load();
    } catch (e) { notify.error(dsErrMsg(e, 'Save failed.')); }
    finally { setSaving(null); }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      const res = await dsMasterCatalogApi.deleteSheet(deleting.id, auditCfg);
      notify.success(`${deleting.indexSheet} removed from the catalog (${res.data.valuesDeleted} master values).`);
      setDeleting(null);
      await load();
    } catch (e) { notify.error(dsErrMsg(e, 'Delete failed.')); }
    finally { setBusy(false); }
  };

  const nav = (
    <div className="stui-segmented">
      {SECTIONS.map(([id, label]) => (
        <button key={id} className={`stui-segmented-btn${section === id ? ' selected' : ''}`} onClick={() => setSection(id)}>{label}</button>
      ))}
    </div>
  );

  if (section === 'compare') return <CatalogCompareSection nav={nav} allDsIndex={allDsIndex} statuses={statuses} onCatalogChanged={load} />;
  if (section === 'templatedb') return <TemplateDbSection nav={nav} />;

  const isSheets = section === 'sheets';
  const total = isSheets ? sheets.length : mfrs.length;
  const shown = isSheets ? shownSheets.length : shownMfrs.length;

  return (
    <>
      <div className="stui-page-header">
        <div className="stui-row-8">
          {nav}
          <span className="stui-text-sm-muted">{shown === total ? `${total}` : `${shown} of ${total}`}</span>
        </div>
        <div className="stui-row-8">
          <div className="stui-search-wrapper">
            <SearchClearIcon size={14} searchTerm={search} onClear={() => setSearch('')} />
            <input className="stui-search-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." title={SEARCH_HINT} />
          </div>
          <button className="stui-btn stui-btn-secondary" onClick={load} disabled={loading} title="Refresh"><RefreshCw size={14} /></button>
        </div>
      </div>

      <div className="stui-detail-fill stui-mt-sm">
        <div className="stui-data-grid-container">
          <div className="stui-data-grid-scroll-area">
            {isSheets ? (
              <table className="stui-data-grid stui-data-grid-auto">
                <thead>
                  <tr><th>Index Sheet</th><th>Description</th><th>Mfr</th><th>Type</th><th>Status</th><th>Datasheet</th><th>Values</th><th /></tr>
                </thead>
                <tbody>
                  {shownSheets.length === 0 && (
                    <tr><td colSpan={8} className="stui-td-empty">
                      {loading ? 'Loading…' : total === 0 ? 'The catalog is empty. Promote Index Sheets from Compare / Review.' : 'No matching results'}
                    </td></tr>
                  )}
                  {shownSheets.map(s => {
                    const current = s.revisions?.find(r => r.current);
                    return (
                      <tr key={s.id}>
                        <td className="stui-mono">{s.indexSheet}</td>
                        <td>{s.description}</td>
                        <td title={s.manufacturer || ''}>{s.manufacturerCode}</td>
                        <td>{s.type}</td>
                        <td>
                          <div className="stui-row-4">
                            <span className={`stui-badge ${statusBadgeClass(s.status)}`}>{s.status || 'Active'}</span>
                            <StuiSelect className="stui-select" value={s.status ?? ''} disabled={saving === s.id}
                              onChange={e => updateSheet(s, { status: e.target.value }, `status set to ${e.target.value}`)}>
                              {!statuses.includes(s.status ?? '') && <option value={s.status ?? ''}>{s.status || '(blank)'}</option>}
                              {statuses.map(st => <option key={st} value={st}>{st}</option>)}
                            </StuiSelect>
                          </div>
                        </td>
                        <td>
                          {(s.revisions?.length ?? 0) > 1 ? (
                            <StuiSelect className="stui-select" value={s.dsConfig ?? ''} disabled={saving === s.id}
                              title="The catalog's current revision of this sheet's datasheet"
                              onChange={e => {
                                const r = s.revisions.find(x => String(x.id) === e.target.value);
                                updateSheet(s, { dsConfig: r.id }, `current revision set to ${r.rev}`);
                              }}>
                              {s.revisions.map(r => (
                                <option key={r.id} value={r.id}>{`${r.dsIndex}${r.published ? '' : ' (draft)'}`}</option>
                              ))}
                            </StuiSelect>
                          ) : (
                            <span className="stui-mono">{s.dsIndex ?? ''}</span>
                          )}
                        </td>
                        <td title={current ? `Master values on rev ${revOf(current.dsIndex)}` : ''}>{current?.valueCount ?? ''}</td>
                        <td>
                          <button className="stui-btn stui-btn-danger-subtle stui-btn-sm" onClick={() => setDeleting(s)} title="Remove from the catalog">
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <table className="stui-data-grid stui-data-grid-auto">
                <thead>
                  <tr><th>Code</th><th>Manufacturer</th><th>Supplier</th><th>City</th><th>State</th><th>Index Sheets</th></tr>
                </thead>
                <tbody>
                  {shownMfrs.length === 0 && (
                    <tr><td colSpan={6} className="stui-td-empty">
                      {loading ? 'Loading…' : total === 0 ? 'No catalog manufacturers yet. They arrive with promoted Index Sheets, or from Compare / Review.' : 'No matching results'}
                    </td></tr>
                  )}
                  {shownMfrs.map(m => (
                    <tr key={m.id}>
                      <td className="stui-mono">{m.indexCode}</td>
                      <td>{m.manufacturer}</td>
                      <td>{m.supplierName}</td>
                      <td>{m.city}</td>
                      <td>{m.state}</td>
                      <td>{m.sheetCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {deleting && (
        <ConfirmModal title={`Remove ${deleting.indexSheet}`} confirmLabel="Remove" danger busy={busy}
          onConfirm={handleDelete} onClose={() => setDeleting(null)}>
          <p className="stui-hint">
            {`${deleting.indexSheet} is removed from the master catalog with all of its master values` +
              `${deleting.revisions?.length ? ` (${deleting.revisions.reduce((n, r) => n + (r.valueCount || 0), 0)} across ${deleting.revisions.length} revision${deleting.revisions.length === 1 ? '' : 's'})` : ''}.`}
          </p>
          <p className="stui-hint">
            Its datasheet revisions stay in the template library and stay Published. Project databases are not touched.
          </p>
        </ConfirmModal>
      )}
    </>
  );
}
